import TileMinedLogic from "../../../logic/TileMinedLogic";
import { Settings } from "../../../Settings";
import Fast from "../../../util/Fast";
import GridTile from "../../grid/GridTile";
import WorldGrid from "../../grid/WorldGrid";
import Vector from "../../math/Vector";
import Missiles from "./Missiles";
import SkillUtil from "../SkillUtil";
import BigNum from "../../math/BigNum";

export default class Missile {
    /**
     * @param {WorldGrid} worldGrid
     * @param {TileMinedLogic} tileMinedLogic
     * @param {Missiles}
     */
    constructor(worldGrid, tileMinedLogic, missiles) {
        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {TileMinedLogic}
         */
        this.tileMinedLogic = tileMinedLogic;
        /**
         * @constant
         * @type {Missiles}
         */
        this.missiles = missiles;

        /**
         * @type {boolean}
         */
        this.active = false;

        /**
         * @constant
         * @type {Vector}
         */
        this.startPosition = new Vector(0, 0);
        /**
         * @constant
         * @type {Vector}
         */
        this.position = new Vector(0, 0);
        /**
         * @constant
         * @type {Vector}
         */
        this.movementUnitVector = new Vector(0, 0);
        /**
         * @constant
         * @type {Vector}
         */
        this.movementVector = new Vector(0, 0);
        /**
         * @type {GridTile}
         */
        this.tile = null;
        /**
         * @type {number}
         */
        this.spriteDirection = 0;

        /**
         * @type {number}
         */
        this.distance = 0;

        /**
         * @constant
         * @type {BigNum}
         */
        this.missileDamage = new BigNum(0);
    }

    resetMissile() {
        this.active = false;
        this.tile = null;
        this.distance = 0;
    }

    /**
     * @param {Vector} startPositionOrigin 
     * @param {number} radians
     * @param {number} spriteDirection
     */
    onMissileFire(startPositionOrigin, radians, spriteDirection) {
        // Make sure we have been reset.
        this.resetMissile();

        // Start at the center instead of the origin.
        this.startPosition.copy(startPositionOrigin);
        this.startPosition.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

        const missileRadians = this.applyRandom(radians);
        this.spriteDirection = spriteDirection;
        this.active = true;

        // Project the position by 1 tile in the direction of fire.
        SkillUtil.projectPosition(this.startPosition, Settings.tile.size, missileRadians, this.position);

        // Calculate the missile damage
        this.tileMinedLogic.calculateMissileDamage(this.missileDamage);

        // Get a unit vector from the current position to the end position.
        this.movementUnitVector.copy(this.position);
        this.movementUnitVector.subtract(this.startPosition);
        Fast.nor(this.movementUnitVector);
    }

    /**
     * @private
     * @param {number} radians 
     * @returns {number}
     */
    applyRandom(radians) {
        const fudge = (Math.PI / 16) * Math.random();
        if (Math.random() < 0.5) {
            return radians - fudge;
        }
        else {
            return radians + fudge;
        }
    }

    /**
     * @param {number} movementPerFrame
     */
    updateForFrame(movementPerFrame) {

        // Get a vector from the current position to the end position.
        this.movementVector.copy(this.movementUnitVector);
        this.movementVector.scale(movementPerFrame);

        this.distance += movementPerFrame;

        // Update the missile position.
        this.position.add(this.movementVector);
        
        this.tile = this.worldGrid.findGridTile(this.position);
        if (!this.tile) {
            this.active = false;
            return;
        }

        if (this.tile) {
            if (!this.tile.lighting.everVisibleToCharacter) {
                this.tile.markTileAsCurrentlyVisible();
            }

            // Find a tile to explode.
            const collisionTile = !this.tile.open ? this.tile : this.findCollisionTile(this.position, this.tile);

            if (collisionTile) {
                if (!collisionTile.lighting.everVisibleToCharacter) {
                    collisionTile.markTileAsCurrentlyVisible();
                }

                this.tileMinedLogic.missileTile(collisionTile, this.missileDamage);

                if (collisionTile.open) {
                    this.missiles.addExplosion(this.position);
                }

                // If we have run out of damage, then we are done.
                if (this.missileDamage.lessThanEqualsZero()) {
                    this.active = false;
                    return;
                }
            }
        }

        // See if we're done because we have traveled to the max distance
        const maxPixelRange = 20 * Settings.tile.size;
        if (this.distance >= maxPixelRange) {
            this.active = false;
            return;
        }
    }

    /**
     * @private
     * @param {Vector} position 
     * @param {GridTile} tile 
     * @returns {GridTile}
     */
    findCollisionTile(position, tile) {

        const width = 3;

        for (let i = 0; i < tile.neighbors.length; i++) {
            const neighbor = tile.neighbors[i];
            if (!neighbor || neighbor.open) {
                continue;
            }

            if (neighbor.intersects(position.x, position.y, width)) {
                return neighbor;
            }
        }

        return null;
    }
}